const SW_PATH = '/sw.js';

export function isPushSupported() {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator
  );
}

export async function requestNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// User preference stored in localStorage — separate from browser permission
export function getPushEnabled() {
  try {
    return localStorage.getItem('agentPushEnabled') !== 'false';
  } catch {
    return true;
  }
}

export function setPushEnabled(enabled) {
  try {
    localStorage.setItem('agentPushEnabled', String(enabled));
  } catch {}
}

async function waitForActivation(reg, timeoutMs = 5000) {
  if (reg.active) return reg;
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(reg), timeoutMs);
    const sw = reg.installing || reg.waiting;
    if (!sw) { clearTimeout(timer); resolve(reg); return; }
    sw.addEventListener('statechange', function onState() {
      if (sw.state === 'activated' || reg.active) {
        clearTimeout(timer);
        sw.removeEventListener('statechange', onState);
        resolve(reg);
      }
    });
  });
}

async function getActiveSwRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) {
      if (existing.active) return existing;
      return waitForActivation(existing);
    }
    const reg = await navigator.serviceWorker.register(SW_PATH);
    return waitForActivation(reg);
  } catch (err) {
    console.warn('SW registration failed:', err);
    return null;
  }
}

export async function showBrowserNotification(title, body, options = {}) {
  if (!isPushSupported()) return null;
  if (!getPushEnabled()) return null;

  // Request permission if not yet granted (user may not have clicked "Enable Notifications")
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return null;

  const notifOptions = {
    vibrate: [300,100,300],
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: options.tag ?? `order-${options.orderId ?? Date.now()}`,
    requireInteraction: options.requireInteraction ?? true,
    data: options.data || { url: '/agent/dashboard' },
    actions: options.actions || [
      { action: 'view',    title: 'View Orders' },
      { action: 'dismiss', title: 'Dismiss'     },
    ],
  };

  // Try SW (required for action buttons and background notifications)
  const reg = await getActiveSwRegistration();
  if (reg && reg.active) {
    try {
      await reg.showNotification(title, notifOptions);
      return;
    } catch (err) {
      console.warn('SW showNotification failed, falling back:', err);
    }
  }

  // Fallback: plain Notification (no action buttons, but always works if permission granted)
  try {
    const notification = new Notification(title, { ...notifOptions, actions: undefined });
    notification.onclick = () => { window.focus(); notification.close(); };
  } catch (err) {
    console.warn('Notification fallback failed:', err);
  }
}

export async function registerServiceWorker() {
  return getActiveSwRegistration();
}

/**
 * Check if notifications are actually working end-to-end.
 * Returns a status object:
 *   { working: true }  — all good
 *   { working: false, reason: '...', actionable: true/false }
 */
export async function getNotificationStatus() {
  if (!isPushSupported()) {
    return {
      working: false,
      reason: 'unsupported',
      message: 'Notifications not supported in this browser',
      actionable: false
    };
  }

  if (Notification.permission === 'denied') {
    return {
      working: false,
      reason: 'denied',
      message: 'Notification permission blocked in browser settings',
      actionable: false
    };
  }

  if (Notification.permission === 'default') {
    return {
      working: false,
      reason: 'default',
      message: 'Notification permission not granted yet',
      actionable: true
    };
  }

  const reg = await getActiveSwRegistration();

  return {
    working: !!reg?.active,
    reason: reg?.active ? null : 'sw-not-active',
    message: reg?.active
      ? 'Notifications are working'
      : 'Service worker not active',
    actionable: !reg?.active
  };
}

/**
 * Force re-register the service worker to fix broken notification state.
 */
export async function reRegisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;

  try {
    const existing = await navigator.serviceWorker.getRegistration();

    if (existing) {
      await existing.update();

      if (existing.active) {
        console.log('📡 SW: Updated existing service worker');
        return existing;
      }
    }

    const reg = await navigator.serviceWorker.register(SW_PATH);
    return waitForActivation(reg);

  } catch (err) {
    console.warn('📡 SW: Re-registration failed:', err);
    return null;
  }
}

export function closeOrderNotification(orderId) {
  if (!navigator.serviceWorker?.controller) return;

  navigator.serviceWorker.controller.postMessage({
    type: 'CLOSE_ORDER_NOTIFICATION',
    tag: `order-${orderId}`,
  });
}

export function closeAllNotifications() {
  if (!navigator.serviceWorker?.controller) return;

  navigator.serviceWorker.controller.postMessage({
    type: 'CLOSE_ALL_NOTIFICATIONS',
  });
}